import type { ChatbotSuggestedAction } from '../../services/llmService.ts'
import {
  buildProfileUrlFromSubitem,
  PROFILE_SUBITEM_TAB_MAPPING,
} from '../navigationHelpers.ts'

type PrimaryCtaSuggestion = { id: string; label: string } | null

const STATIC_CHATBOT_ACTION_URLS: Record<string, string> = {
  'presupuestos-obra': '/obrasprivadas/crear',
  'cotizar-obras': '/obrasprivadas/explorar',
  'obras-publicas': '/obraspublicas',
  'estimacion-costos': '/cotizaciondemateriales/estimacion-costos',
  'cotizar-materiales': '/cotizaciondemateriales/crear',
  'cotizaciones-materiales': '/cotizaciondemateriales/explorar',
  'encontrar-profesionales': '/buscar-perfiles',
  'postularme-ofertas': '/bolsa-trabajo',
  'publicar-oferta-trabajo': '/mercado-de-trabajo/crearoferta',
  'explorar-servicios': '/marketplace/explorar',
  'comercializar-documentacion': '/documentacion-de-obra/crear',
  backoffice: '/backoffice'
}

export function parseSuggestedActionsFromLlmRaw(
  llmRawResponse?: string | null
): ChatbotSuggestedAction[] {
  if (!llmRawResponse) return []
  try {
    const parsed = JSON.parse(llmRawResponse) as { suggestedActions?: unknown }
    const raw = parsed?.suggestedActions
    if (!Array.isArray(raw)) return []
    const out: ChatbotSuggestedAction[] = []
    for (const item of raw) {
      if (item && typeof item === 'object' && 'actionId' in item && 'label' in item) {
        const actionId = String((item as { actionId: unknown }).actionId ?? '').trim()
        const label = String((item as { label: unknown }).label ?? '').trim()
        if (actionId && label) out.push({ actionId, label })
      }
    }
    return out.slice(0, 5)
  } catch {
    return []
  }
}

export function filterSuggestedActionsDedupPrimary(
  actions: ChatbotSuggestedAction[],
  message: { actionId?: string | null },
  primarySuggestion: PrimaryCtaSuggestion
): ChatbotSuggestedAction[] {
  if (!primarySuggestion || !message.actionId || message.actionId !== primarySuggestion.id) {
    return actions
  }
  const primaryLabel = primarySuggestion.label.trim().toLowerCase()
  return actions.filter(
    (a) =>
      !(
        a.actionId === message.actionId &&
        a.label.trim().toLowerCase() === primaryLabel
      )
  )
}

export function getChatbotActionUrl(actionId: string, userData?: any): string | null {
  if (typeof window === 'undefined') return null
  const baseUrl = window.location.origin
  if (STATIC_CHATBOT_ACTION_URLS[actionId]) {
    return `${baseUrl}${STATIC_CHATBOT_ACTION_URLS[actionId]}`
  }
  if (PROFILE_SUBITEM_TAB_MAPPING[actionId] && userData) {
    const userName =
      userData.type === 'COMPANY'
        ? userData.razon_social
        : `${userData.name} ${userData.last_name}`
    const profileUrl = buildProfileUrlFromSubitem(
      String(userData.id),
      userName,
      actionId
    )
    return `${baseUrl}${profileUrl}`
  }
  return null
}

export function shouldOpenChatbotActionInNewWindow(actionId: string): boolean {
  return actionId !== 'ofrecer-servicios'
}

const CHAT_FIRST_SUGGESTED_ACTION_IDS = new Set([
  'project-autofill',
  'derivar-especialista-proyecto',
  'post-agent',
  'crear-post',
  'crear-portfolio',
  'volver-general',
  'comercializar-documentacion'
])

export function isSuggestedActionChatFirst(actionId: string): boolean {
  return CHAT_FIRST_SUGGESTED_ACTION_IDS.has(actionId)
}
